/**
 * Config Adapter - Normalizes V3 reporting configs to V4 format
 * 
 * Handles backward compatibility by:
 * - Transforming V3 dateRange filters to V4 format
 * - Removing obsolete filter types (select, columnFilter, text)
 * - Deduplicating dateRange filters from mixed V3/V4 merges
 */

import { ReportGridConfig, ReportFilterConfig } from '../app-types';
import type { EventConfig } from '../features/report-timeline/report-timeline-types';
import { logger } from '@personicom/customizations';
import type { ReportingConfig } from '@personicom/customizations';

const REQUIRED_LOOKUP_EVENTS: Record<string, EventConfig> = {
  'ai.VideoRequested': {
    displayName: 'Video Lookup Requested',
    displayProp: '{tags.endpointPurpose}',
    icon: 'manage_search'
  },
  'ai.VideoReady': {
    displayName: 'Video Lookup Ready',
    displayProp: '{tags.endpointPurpose}',
    icon: 'check_circle'
  }
};

/**
 * Normalizes V3 reporting configs to V4 format for backward compatibility
 */
export function normalizeReportingConfig(config: ReportingConfig): ReportGridConfig {
  const typedConfig = config as ReportGridConfig;
  const workingConfig: ReportGridConfig = {
    ...typedConfig,
    idField: typedConfig.idField ?? 'renderId',
    columns: typedConfig.columns ?? []
  };

  const originalFilterCount = workingConfig.filters?.length || 0;

  let filters = workingConfig.filters
      ?.map(filter => normalizeFilter(filter, workingConfig.columns))
      .filter((f): f is ReportFilterConfig => f !== null);

  // Deduplicate dateRange filters (handles V4 root + V3 subdomain merging)
  if (filters && filters.length > 0) {
    filters = deduplicateDateRangeFilters(filters);
  }

  const normalized: ReportGridConfig = {
    ...workingConfig,
    filters,
    events: ensureRequiredLookupEvents(workingConfig.events)
  };

  const removedCount = originalFilterCount - (filters?.length || 0);
  
  if (removedCount > 0) {
    logger.info(
      `[Config] Removed ${removedCount} obsolete/duplicate filter(s)`,
      { original: originalFilterCount, normalized: normalized.filters?.length },
      'Config'
    );
  }
  
  return normalized;
}

/**
 * Ensures lookup lifecycle event labels exist even when tenant config has not been updated yet.
 */
function ensureRequiredLookupEvents(events?: Record<string, EventConfig>): Record<string, EventConfig> {
  return {
    ...REQUIRED_LOOKUP_EVENTS,
    ...(events ?? {})
  };
}

/**
 * Finds the actual column field name matching the filter key (case-insensitive lookup)
 */
function findMatchingColumnField(filterKey: string, columns: ReportGridConfig['columns']): string {
  const match = columns?.find(col => col.field.toLowerCase() === filterKey.toLowerCase());
  return match?.field ?? filterKey;
}

/**
 * Normalizes individual filter config
 * - V3: dateRange with nested keys (startDate/endDate)
 * - V4: dateRange with columnField key (QueuedOnUTC) and no nested keys
 * - Also removes obsolete filter types that are now handled by DataGrid
 * - Matches filter key to actual column field name for case-sensitivity
 */
function normalizeFilter(filter: ReportFilterConfig, columns: ReportGridConfig['columns']): ReportFilterConfig | null {
  // Remove obsolete filter types - DataGrid handles these natively now
  // date: standalone hidden date filters (V3 legacy, replaced by dateRange)
  // select: dropdown filters (replaced by DataGrid column filtering)
  // columnFilter: column selection (replaced by DataGrid column menu)
  // text: text search (replaced by DataGrid quick filter)
  if (filter.type === 'date' || filter.type === 'select' || filter.type === 'columnFilter' || filter.type === 'text') {
    logger.verbose(
      `[Config] Skipping obsolete filter type "${filter.type}" for key "${filter.key}"`,
      { filter },
      'Config'
    );
    return null;
  }
  
  if (filter.type === 'dateRange' && filter.options) {
    const hasNestedKeys = filter.options.rangeStart?.key || filter.options.rangeEnd?.key;
    
    // V3 format detected: has nested keys
    if (hasNestedKeys) {
      // Transform V3 → V4 by extracting column field from nested structure
      // V3 typically uses generic "dateRange" as key, but we need the actual column
      // Default to "queuedOnUTC" if not specified, as that's the standard date field
      // IMPORTANT: Look up actual column field for exact case match
      const columnField = findMatchingColumnField(filter.key === 'dateRange' ? 'queuedOnUTC' : filter.key, columns);
      
      logger.verbose(
        `[Config] Normalizing V3 dateRange filter from "${filter.key}" to "${columnField}"`,
        { originalKey: filter.key, normalizedKey: columnField },
        'Config'
      );
      
      return {
        ...filter,
        key: columnField, // Use actual column field as key (lowercase)
        options: {
          ...filter.options,
          rangeStart: {
            ...filter.options.rangeStart,
            // Preserve the nested key for backward compat but ensure it exists
            key: filter.options.rangeStart?.key ?? 'startDate'
          },
          rangeEnd: {
            ...filter.options.rangeEnd,
            key: filter.options.rangeEnd?.key ?? 'endDate'
          }
        }
      };
    }
    
    // V4 format: ensure nested keys exist for DateRangeFilter component
    // Match key to actual column field for exact case match
    return {
      ...filter,
      key: findMatchingColumnField(filter.key, columns),
      options: {
        ...filter.options,
        rangeStart: {
          ...filter.options.rangeStart,
          key: filter.options.rangeStart?.key ?? 'startDate'
        },
        rangeEnd: {
          ...filter.options.rangeEnd,
          key: filter.options.rangeEnd?.key ?? 'endDate'
        }
      }
    };
  }
  
  return filter;
}

/**
 * Deduplicates dateRange filters that target the same column
 * Handles case where root has V4 dateRange and subdomain has V3 dateRange
 */
function deduplicateDateRangeFilters(filters: ReportFilterConfig[]): ReportFilterConfig[] {
  const dateRangeFilters = filters.filter(f => f.type === 'dateRange');
  const otherFilters = filters.filter(f => f.type !== 'dateRange');
  
  if (dateRangeFilters.length <= 1) {
    return filters; // No duplicates
  }
  
  // Keep the most specific dateRange filter (non-generic key preferred)
  // This handles V4 root + V3 subdomain merging where both may have dateRange filters
  const preferredFilter = dateRangeFilters.find(f => f.key !== 'dateRange' && f.key !== 'queuedOnUTC') 
    || dateRangeFilters.find(f => f.key !== 'dateRange')
    || dateRangeFilters[0];
  
  logger.verbose(
    `[Config] Deduplicated ${dateRangeFilters.length} dateRange filters to 1`,
    { 
      kept: preferredFilter.key, 
      removed: dateRangeFilters.filter(f => f.key !== preferredFilter.key).map(f => f.key) 
    },
    'Config'
  );
  
  return [...otherFilters, preferredFilter];
}

/**
 * Detects if config is using V3 format
 */
export function isV3Config(config: ReportingConfig): boolean {
  return (config as ReportGridConfig).filters?.some(f => 
    f.type === 'dateRange' && 
    f.key === 'dateRange' &&
    (f.options?.rangeStart?.key || f.options?.rangeEnd?.key)
  ) ?? false;
}
